import React, { type FC, useMemo } from "react";
import { BusinessLotValue, BusinessMeasureValue, BusinessStockObjValue } from "@src/components/business-data-display";
import { DetailList } from "@shared/ui/detail-row";
import { EmptyState } from "@shared/ui/empty-state";
import FormFooter from "@shared/ui/form-footer";
import { FormPanel } from "@shared/ui/form-panel";
import type { OnsideInvoiceContent } from "../../../../../../api/onsideInvoices";
import {
    formatShipmentCellsFromSpMovings,
    formatShipmentOrderText,
} from "../../helpers/formatStockFinishedGoodsShipmentContent";
import type { UseStockFinishedGoodsShipmentCardMutationsResult } from "../../useStockFinishedGoodsShipmentCardMutations";
import {
    stockFinishedGoodsShipmentContentFormConfig,
    type StockFinishedGoodsShipmentContentFormContext,
    type StockFinishedGoodsShipmentContentFormValues,
} from "./stockFinishedGoodsShipmentContent.form-config";

/** Пропсы компонента {@link StockFinishedGoodsShipmentContentForm}. */
export interface StockFinishedGoodsShipmentContentFormProps {
    /** Код строки состава НС. */
    contentCode: number;
    /** Строки текущей НС. */
    contents: OnsideInvoiceContent[];
    /** Мутация установки признака подтверждения отгрузки. */
    setConfirmLeaveMutation: UseStockFinishedGoodsShipmentCardMutationsResult["setConfirmLeaveMutation"];
}

/** Форма подтверждения отгрузки продукции по строке НС. */
export const StockFinishedGoodsShipmentContentForm: FC<StockFinishedGoodsShipmentContentFormProps> = ({
    contentCode,
    contents,
    setConfirmLeaveMutation,
}) => {
    const content = contents.find((item) => item.Code === contentCode);

    if (content == null) {
        return <EmptyState text="Строка НС не найдена" />;
    }

    return <StockFinishedGoodsShipmentContentFormBody content={content} setConfirmLeaveMutation={setConfirmLeaveMutation} />;
};

interface StockFinishedGoodsShipmentContentFormBodyProps {
    content: OnsideInvoiceContent;
    setConfirmLeaveMutation: UseStockFinishedGoodsShipmentCardMutationsResult["setConfirmLeaveMutation"];
}

const StockFinishedGoodsShipmentContentFormBody: FC<StockFinishedGoodsShipmentContentFormBodyProps> = ({
    content,
    setConfirmLeaveMutation,
}) => {
    const nextConfirmLeave = !content.ConfirmLeave;
    const formContext = useMemo(
        (): StockFinishedGoodsShipmentContentFormContext => ({
            contentCode: content.Code,
            confirmLeave: nextConfirmLeave,
        }),
        [content.Code, nextConfirmLeave],
    );
    const boundFormConfig = useMemo(
        () => stockFinishedGoodsShipmentContentFormConfig.withContext(formContext),
        [formContext],
    );
    const initialValues = useMemo((): StockFinishedGoodsShipmentContentFormValues => ({}), []);

    const handleSubmit = async (_values: StockFinishedGoodsShipmentContentFormValues): Promise<void> => {
        await setConfirmLeaveMutation.mutateAsync({
            code: content.Code,
            confirmLeave: nextConfirmLeave,
        });
    };

    return (
        <FormPanel<StockFinishedGoodsShipmentContentFormValues>
            useDefaultFooter={false}
            formProps={{
                formConfig: boundFormConfig,
                initialValues,
                ready: true,
                onSubmit: handleSubmit,
            }}
            footer={({ submitForm, isFormSubmitting }) => (
                <FormFooter
                    confirmText={content.ConfirmLeave ? "Отменить подтверждение" : "Отгрузить"}
                    confirmLoading={isFormSubmitting}
                    onConfirm={submitForm}
                />
            )}
        >
            {() => (
                <DetailList layout="tiles" columns="auto" minWidth={112}>
                    <DetailList.Row label="Номенклатура" value={<BusinessStockObjValue stockObj={content.StockObj} />} />
                    <DetailList.Row label="Партия" value={<BusinessLotValue lot={content.Lot} />} />
                    <DetailList.Row
                        label="Количество"
                        value={<BusinessMeasureValue value={content.Qty} measure={content.Measure} />}
                    />
                    <DetailList.Row label="Заказ" value={formatShipmentOrderText(content.Order)} />
                    <DetailList.Row label="Ячейки размещения" value={formatShipmentCellsFromSpMovings(content.SpMovings)} />
                    <DetailList.Row label="Подтверждение отгрузки" value={content.ConfirmLeave ? "Да" : "Нет"} />
                </DetailList>
            )}
        </FormPanel>
    );
};
